import {
  Box,
  Button,
  chakra,
  Flex,
  Image,
  shouldForwardProp,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { parseSomething } from "../../utils/parseSomething";
import { useDispatch, useSelector } from "react-redux";
import { isValidMotionProp, motion, AnimatePresence } from "framer-motion";
import { COMMON_WEB_PATH } from "../../constant";
import Count from "./components/Count";
import dayjs from "dayjs";
import useSound from "../../hook/useSound";
import {
  CLEAR_SOCKET_MESSAGE,
  notice,
  SAVE_SOCKET_MESSAGE,
} from "../../redux/action/action";
import { useTranslation } from "react-i18next";
import {
  SHOW_MAINTAIN_ALERT,
  storeCurrencyExchange,
  TRANSFER_READY,
  triggerTransferGivenUpPoint,
} from "../../redux/action/member/memberAction";

import earningMusic from "../../images/music/earning-money.mp3";
import useWebsiteInfo from "../../hook/useWebsiteInfo";
import useCheckLogin from "../../hook/useCheckLogin";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTransferInGameHandler } from "../Game/components/TransferInGame/transferInGameHandler";
import useIsLandScapeMachine from "../../hook/useIsLandScapeMachine";
import { formatNumber } from "../../utils/formatNumber";
import MainBalancePending from "./components/MainBalancePending";
import DepositLimitAlert from "./components/DepositLimitAlert";
// import TestDepositButton from "./components/TestDepositButton";

const gradientStyle = {
  background:
    "linear-gradient(180deg, rgba(2,0,36,1) 0%, rgba(247,249,79,1) 0%, rgba(254,238,54,1) 50%, rgba(245,174,12,1) 75%, rgba(236,208,59,1) 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const animationVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1.5,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 20,
      mass: 0.5,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  exit: {
    scale: 0,
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

const HEARTBEAT_INTERVAL = 30000;

const EarnMoneyFromSocket = () => {
  const { t } = useTranslation();
  const i18n_common = (key, props) => t(`common.${key}`, { ...props });
  const i18n_backend = (key) => t(`backend_response.${key}`);
  const { images } = useWebsiteInfo();

  const toast = useToast();

  const [socketUrl] = useState(process.env.REACT_APP_WS_HOST);
  const [messages, setMessages] = useState([]);
  const messageIdRef = useRef(0);
  const generateMessageId = () => `${Date.now()}-${messageIdRef.current++}`;
  const [openDepositLimitAlert, setOpenDepositLimitAlert] = useState(false);
  const [depositLimitData, setDepositLimitData] = useState({
    amount: 0,
    limit_amount: 0,
  });

  const { handleTransferAllIn } = useTransferInGameHandler();

  const isLandscapeMachine = useIsLandScapeMachine();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const memberInfo = useSelector((state) => state.isMemberInfo);
  const isSocketMessage = useSelector((state) => state.isSocketMessage);
  const isTransferInGameState = useSelector(
    (state) => state.isTransferInGameState,
  );
  const gameConfig = useSelector((state) => state.isGameConfig);

  const { message, show, rawMessage } = isSocketMessage || {};

  const { uid, cagent_uid } = memberInfo || {};

  // 從 Redux 取得當前遊戲 UID
  const currentGameUid = gameConfig?.gameUid ?? null;
  const { isLogin } = useCheckLogin();

  const earningSound = useSound({ customSound: "earning_money" });

  const didUnmount = useRef(false);

  const {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    lastJsonMessage,
    readyState,
    getWebSocket,
  } = useWebSocket(socketUrl, {
    queryParams: {
      key: "wsky",
    },
    onOpen: () => console.log("連線成功"),
    shouldReconnect: (closeEvent) => {
      /*
      useWebSocket will handle unmounting for you, but this is an example of a 
      case in which you would not want it to automatically reconnect
    */
      return didUnmount.current === false;
    },
    reconnectAttempts: 9999,
    reconnectInterval: 1000,
  });

  const socketReceivedData = useMemo(() => {
    if (lastMessage?.data) {
      return parseSomething(lastMessage.data);
    }
  }, [lastMessage?.timeStamp]);

  const compareMemberData = useMemo(() => {
    if (socketReceivedData?.member_info_uid == uid) {
      return true;
    }
    return false;
  }, [socketReceivedData, uid]);

  const handleConfirm = () => {
    dispatch(CLEAR_SOCKET_MESSAGE());
  };

  const handleCloseMessage = (id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  const responseSocketModal = useMemo(() => {
    if (show) {
      return (
        <Flex
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.8)"
          zIndex="9999"
          backdropFilter="blur(2px)"
          justifyContent="center"
          alignItems="center"
        >
          <ChakraBox
            as={Flex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            w="85%"
            maxWidth={"520px"}
            h="auto"
            maxHeight={"480px"}
            position={"relative"}
            bgImage={`url('${COMMON_WEB_PATH}/911TW/hintBG2.webp')`}
            bgPosition="center"
            bgRepeat="no-repeat"
            bgSize="100% 100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
            p={6}
            pt="30px"
          >
            <Flex
              fontSize="24px"
              color="white"
              textAlign="center"
              mt={4}
              bgImage={`url('${COMMON_WEB_PATH}/FlyDragon/hintTitleBG.webp')`}
              bgPosition="center"
              bgRepeat="no-repeat"
              bgSize="100% 100%"
              sx={{ width: "-webkit-fill-available" }}
              justifyContent="center"
              alignItems="center"
              position="relative"
            >
              <Image
                src={`${COMMON_WEB_PATH}/FlyDragon/hintWORD.webp`}
                height={{
                  base: "50px",
                  xl: "60px",
                }}
              />
            </Flex>
            <Box
              position="relative"
              my="15px"
              mb="50px"
              fontSize="30px"
              fontWeight="black"
              color="white"
              textAlign="center"
              sx={{
                background:
                  "linear-gradient(180deg, #fff 0%,#fdd26a 60%,  #b07f20 80%,  #b07f20 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                textShadow: "0px 1px 3px rgba(255, 215, 0, 0.4)",
              }}
            >
              {rawMessage ? message : i18n_backend(message)}
            </Box>
            <Flex
              gap={4}
              mb={4}
              justifyContent="space-between"
              alignItems="center"
            >
              <Flex
                as="button"
                w="165px"
                h="62px"
                bgImage={`url('${images.yesBTN}')`}
                bgPosition="center"
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                justifyContent="center"
                alignItems="center"
                onClick={handleConfirm}
                position="relative"
                zIndex={1}
              >
                {/* <Image
                  src={`${COMMON_WEB_PATH}/FlyDragon/yesWORD.webp`}
                  width="70px"
                  height={{
                    base: "30px",
                    xl: "30px",
                  }}
                /> */}
              </Flex>
            </Flex>
          </ChakraBox>
        </Flex>
      );
    }
  }, [show, message]);

  const responseComponents = (
    <Box
      position="fixed"
      top="55%"
      left="50%"
      transform="translate(-50%, -50%)"
      display={"flex"}
      alignItems={"center"}
      justifyContent={"center"}
      zIndex={999999}
    >
      <Flex direction={"column"} gap="10px" alignItems={"center"}>
        <AnimatePresence>
          {messages?.map((msg) => {
            const { amount = 0, id } = msg;
            return (
              <ChakraBox
                key={id}
                variants={animationVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                bg={"rgba(0,0,0,0.5)"}
                px="30px"
                py="10px"
                borderRadius={"common"}
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                bgPosition="center"
                position="relative"
              >
                <Box
                  as="button"
                  position="absolute"
                  top="6px"
                  right="10px"
                  color="white"
                  fontSize="20px"
                  lineHeight="1"
                  cursor="pointer"
                  onClick={() => handleCloseMessage(id)}
                  zIndex={1}
                >
                  ✕
                </Box>
                <Box position="relative">
                  <Image
                    src={`${COMMON_WEB_PATH}/911TW/moneyPlus.png`}
                    alt="bg"
                    position="absolute"
                    top="-50px"
                    left="50%"
                    transform="translateX(-50%)"
                    pointerEvents="none"
                  />
                  <Flex
                    gap="10px"
                    alignItems="center"
                    fontSize="90px"
                    fontWeight={900}
                    {...gradientStyle}
                  >
                    <Text>+</Text>
                    <Text>
                      <Count from={0} to={Number(amount)} />
                    </Text>
                  </Flex>
                </Box>
              </ChakraBox>
            );
          })}
        </AnimatePresence>
      </Flex>
    </Box>
  );

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    if (socketReceivedData) {
      const { type, member_info_uid, data, memo } = socketReceivedData;
      if (type === "deposit" && member_info_uid === uid && data != null) {
        const { amount, exchange } = data || {};
        if (amount == "error") {
          return dispatch(
            notice({
              title: i18n_backend("Server Error"),
              type: "error",
            }),
          );
        }
        dispatch(storeCurrencyExchange(exchange || {}));

        /**
         * @description 當收到存款的訊息時，會觸發all-in-la API
         */
        handleTransferAllIn();

        new Audio(earningMusic).play();

        const messageWithId = {
          amount: amount,
          id: generateMessageId(),
          isVisible: true,
        };
        setMessages((prev) => [...prev, messageWithId]);

        setTimeout(() => {
          setMessages((prev) => prev.filter((msg) => msg.id !== messageWithId.id));
        }, 4000);
      } else if (
        type === "transCode" &&
        member_info_uid === uid &&
        data != null
      ) {
        if (data == "fails") {
          dispatch(
            SAVE_SOCKET_MESSAGE({
              message: memo,
              show: true,
            }),
          );
        } else {
          dispatch(CLEAR_SOCKET_MESSAGE());
          dispatch(triggerTransferGivenUpPoint(true));

          setTimeout(() => {
            //RESET
            dispatch(triggerTransferGivenUpPoint(false));
          }, 5000);
        }
      } else if (type === "error" && member_info_uid === uid && data != null) {
        dispatch(
          SAVE_SOCKET_MESSAGE({
            message: data,
            show: true,
          }),
        );
      } else if (type === "update_member") {
        //會員資料更新
        if (isLogin) {
          dispatch({ type: "GET_MEMBER_DATA" });
        }
      } else if (type === "maintain_notice") {
        dispatch(
          SHOW_MAINTAIN_ALERT({
            state: true,
            maintainTime: data,
          }),
        );
      } else if (type === "maintain_notice_member") {
        if (data?.cagent_uid != null) {
          if (String(cagent_uid) === String(data.cagent_uid)) {
            dispatch(
              SAVE_SOCKET_MESSAGE({
                message: data.msg,
                show: true,
                rawMessage: true,
              }),
            );
            setTimeout(() => {
              navigate("/");
            }, 5000);
          }
          return;
        } else if (Number(member_info_uid) === Number(uid)) {
          if (data?.game_id != null) {
            if (
              Array.isArray(data.game_id) &&
              data.game_id.some((id) => String(id) === String(currentGameUid))
            ) {
              dispatch(
                SAVE_SOCKET_MESSAGE({
                  message: data.msg,
                  show: true,
                  rawMessage: true,
                }),
              );
              setTimeout(() => {
                navigate("/");
              }, 5000);
            }
            return;
          }

          dispatch(
            SAVE_SOCKET_MESSAGE({
              message: data,
              show: true,
            }),
          );
          setTimeout(() => {
            window.location.reload();
          }, 50000);
        }
      } else if (type == "reload") {
        window.location.reload();
      } else if (type == "transfer_in_game" || type == "CHECK_RREQUEST") {
        const { request_id, amount, message, platform, status } = data || {};
        if (member_info_uid !== uid) return;

        const current_request_id = localStorage.getItem(
          "transfer_in_game_request_id",
        );

        if (status === "pending") {
          localStorage.setItem("transfer_in_game_request_id", request_id);

          return;
        } else {
          if (current_request_id == request_id) {
            if (status === "rollback") {
              handleTransferAllIn();
              return;
            }
            localStorage.removeItem("transfer_in_game_request_id");

            dispatch(TRANSFER_READY());
            toast({
              title: i18n_common("提醒您"),
              description: i18n_common(status, {
                amount: formatNumber(amount),
                platform: platform,
                status: status,
              }),
              status: "success",
              duration: 7000,
              isClosable: true,
              position: isLandscapeMachine ? "bottom" : "top",
            });
          }
        }
      } else if (
        type === "cash_register_limit" &&
        Number(member_info_uid) === uid
      ) {
        const { amount, limit_amount } = data || {};
        // amount 是當前入鈔額度
        // limit_amount 是總額度上限
        setDepositLimitData({ amount, limit_amount });
        setOpenDepositLimitAlert(true);
      } else if (type === "update_only_one_member") {
        if (isLogin) {
          if (Number(member_info_uid) === uid) {
            dispatch({ type: "GET_MEMBER_DATA" });
          }
        }
      }
    }
  }, [socketReceivedData, isLandscapeMachine, uid, currentGameUid]);

  useEffect(() => {
    const heartbeat = setInterval(() => {
      sendMessage("ping");
    }, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(heartbeat);
    };
  }, [sendMessage]);

  useEffect(() => {
    /**
     * @description 這邊是處理遊戲中轉點的邏輯，發生於101,102
     * 用於當用戶重整時，會將lcoalStorage中的轉點請求單號傳給後端，後端會根據單號回傳結果
     * 邏輯的結果處理依照WebSocket回傳的type來決定
     */
    const current_request_id = localStorage.getItem(
      "transfer_in_game_request_id",
    );

    if (current_request_id && uid && isTransferInGameState === "pending") {
      sendJsonMessage({
        type: "CHECK_RREQUEST",
        request_id: current_request_id,
        member_info_uid: uid,
      });
    }
  }, [uid, isTransferInGameState]);
  const handleTestDeposit = () => {
    const testAmount = 888;
    const messageWithId = {
      amount: testAmount,
      id: generateMessageId(),
      isVisible: true,
    };
    setMessages((prev) => [...prev, messageWithId]);
    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== messageWithId.id));
    }, 4000);
  };

  return (
    <Box zIndex={999999} position={"relative"}>
      {responseComponents}
      <MainBalancePending />
      {responseSocketModal}
      <DepositLimitAlert
        openModal={openDepositLimitAlert}
        setOpenModal={setOpenDepositLimitAlert}
        limitConfig={depositLimitData}
        setLimitConfig={setDepositLimitData}
      />
      {/* 測試用：手動觸發存款動畫，需要時再開啟 */}
      {/* <TestDepositButton onTest={handleTestDeposit} /> */}
    </Box>
  );
};

export default EarnMoneyFromSocket;
